import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { AppShell } from "@/components/app/app-shell";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminActivityQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/t1dwpanel/activity")({
  head: () => ({
    meta: [
      { title: "Admin Activity | Warrior Volunteer" },
      { name: "description", content: "Admin Activity in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Admin Activity | Warrior Volunteer" },
      { property: "og:description", content: "Admin Activity in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminActivityPage,
});

function AdminActivityPage() {
  const { data: activity = [] } = useQuery(adminActivityQuery);
  const [search, setSearch] = useState("");
  const [volunteer, setVolunteer] = useState("all");
  const [type, setType] = useState("all");
  const [program, setProgram] = useState("all");

  const volunteers = useMemo(
    () => Array.from(new Set(activity.map((r) => r.volunteer_name))).sort(),
    [activity],
  );
  const types = useMemo(
    () => Array.from(new Set(activity.map((r) => r.activity_type))).sort(),
    [activity],
  );
  const programs = useMemo(
    () => Array.from(new Set(activity.map((r) => r.programs?.name ?? "-"))).sort(),
    [activity],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activity.filter((row) => {
      if (volunteer !== "all" && row.volunteer_name !== volunteer) return false;
      if (type !== "all" && row.activity_type !== type) return false;
      if (program !== "all" && (row.programs?.name ?? "-") !== program) return false;
      if (!q) return true;
      return [row.volunteer_name, row.activity_type, row.notes ?? "", row.programs?.name ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [activity, search, volunteer, type, program]);

  const totalHours = rows.reduce((sum, row) => sum + Number(row.hours ?? 0), 0);

  return (
    <AppShell eyebrow="Admin" title="Program activity">
      <div className="panel mb-4 flex items-center justify-between p-5">
        <p className="t-meta">
          {rows.length === activity.length
            ? "Total hours across all volunteers"
            : `Hours in this filter (${rows.length} of ${activity.length} entries)`}
        </p>
        <p className="tabular-nums text-2xl font-bold text-cyan">
          {Math.round(totalHours * 100) / 100}
        </p>
      </div>

      <div className="panel mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search volunteer or notes"
        />
        <Select value={volunteer} onValueChange={setVolunteer}>
          <SelectTrigger>
            <SelectValue placeholder="Volunteer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All volunteers</SelectItem>
            {volunteers.map((name) => (
              <SelectItem key={name} value={name}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={program} onValueChange={setProgram}>
          <SelectTrigger>
            <SelectValue placeholder="Program" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All programs</SelectItem>
            {programs.map((p) => (
              <SelectItem key={p} value={p}>
                {p === "-" ? "No program" : p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="panel overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Volunteer</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  {activity.length === 0
                    ? "No activity logged yet."
                    : "No activity matches those filters."}
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="t-meta">{row.activity_date}</TableCell>
                <TableCell className="font-medium">
                  <span>{row.volunteer_name}</span>
                  <Badge
                    variant={row.volunteer_tier === "volunteer" ? "default" : "secondary"}
                    className="ml-2 align-middle"
                  >
                    {row.volunteer_tier === "volunteer" ? "Volunteer" : "Helper"}
                  </Badge>
                </TableCell>
                <TableCell className="text-ice">{row.activity_type}</TableCell>

                <TableCell>{row.programs?.name ?? "-"}</TableCell>
                <TableCell className="tabular-nums font-semibold">{row.hours}</TableCell>
                <TableCell className="max-w-sm truncate">{row.notes ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </AppShell>
  );
}
